import type { FormEvent, ReactNode } from 'react';
import { Button } from '@/components/base/Button';
import { t } from '@/messages/zh-TW';
import type { EffectKey } from '@/mocks/endpoint-map';
import type { LegacyFormSpec } from '@/mocks/legacy-form-specs';

/**
 * 第二層版型：查詢條件區。
 *
 * 原系統對應：各列表頁上方的 `<form>` + `.ibox-content` 內的 `form-horizontal` 欄位群，
 * 底部固定一組「查詢／清除」按鈕（例：`config/role.jsp`、`config/account.jsp` 的查詢區）。
 * 送出走 form submit（Enter 亦可觸發），這一點刻意保留成原生 `<form onSubmit>`，
 * 不改成純 onClick —— 原系統在欄位裡按 Enter 就會查詢。
 *
 * ponytail: 欄位本身用 children 傳入（通常是 <FormGrid>），本元件只負責
 * 「殼 + 送出／清除 + Enter 行為」。不做欄位 schema 驅動——雛形階段欄位形狀還會變。
 *
 * 【證據等級】§7 第 3 級（原始碼）。【雛形值待實機校準】
 */
export interface SearchPanelProps {
  uiId: string;
  /** 查詢條件欄位。 */
  children: ReactNode;
  onSearch(): void;
  /** 沒有傳就不畫「清除」鈕。 */
  onReset?(): void;
  /** 送出中（例如查詢請求還沒回來）時鎖住按鈕。 */
  busy?: boolean;
  /** 按鈕列左側的額外操作（例如「新增」）。 */
  extraActions?: ReactNode;
  /**
   * 「查詢」鈕的 `data-effect`（D-1）。呼叫端只有在 `onSearch` 真的會打
   * `useDataSource().list()` 時才可以傳（criteria C5-j）。
   * 「清除」鈕**不傳**：它只清 state，不打資料源。
   */
  searchEffect?: EffectKey;
  /**
   * W11（批次 42，issue #40，裁決④案 B）：legacy `<form>` 的 action/method 鏡像
   * （GNL-005-b 批次 36 的 spec 機制；SearchPanel 只對呼叫端**明確傳入**的 spec 生效）。
   * CrudListPage 傳 `formSpecFor(pageId)`＝白名單制：非白名單頁 undefined＝裸 form（零影響）。
   */
  formSpec?: LegacyFormSpec;
  /**
   * 批次 43（#41 GNL-005-e，裁決③）：legacy 查詢 form 右欄的說明區（col-md-5，form 的
   * **sibling**；queryPage.jsp:211-218「說明:+p+ol×3」、fieldTypeList.jsp:226-231、
   * teacherPoolList.jsp:398-403 等）。未傳＝零影響。內容由呼叫端（頁行為/地圖）提供，
   * 本元件只負責「畫在 form 後」的位置語意。
   */
  asideNote?: ReactNode;
  /**
   * 批次 43（#41）：查詢鈕**之後**的按鈕群（queryPage.jsp:186-207：查詢/啟用/停用/刪除/
   * 變更為未完成同一排）。extraActions 在查詢鈕之前（legacy 工具列語意），tailActions 在
   * 其後（legacy form 動作列語意）。未傳＝零影響。
   */
  tailActions?: ReactNode;
  /**
   * 批次 43（#41）：查詢 form **頂部**（fields 之前）的工具列（studyRecordFill.jsp:243-247：
   * 一次填寫多類鈕＋說明文字＝form[1] 的前兩個子節點）。未傳＝零影響。
   */
  topActions?: ReactNode;
}

export function SearchPanel({
  uiId,
  children,
  onSearch,
  onReset,
  busy = false,
  extraActions,
  searchEffect,
  formSpec,
  asideNote,
  tailActions,
  topActions,
}: SearchPanelProps) {
  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSearch();
  }

  // noValidate：查詢區裡的欄位是從原系統抽出來的契約欄位，其中有 `required` 的欄位
  // （required 是功能契約要比對的屬性，不能為了送得出去就拿掉）。瀏覽器與 jsdom 的
  // 互動式驗證會在必填欄位為空時**整個擋掉 submit 事件**，查詢鈕就再也打不到資料源
  // （criteria C5-j 的假動作哨兵會抓到）。這一頁本來就不做原生送出（handleSubmit
  // 一律 preventDefault），關掉原生驗證不改變任何行為。
  // `novalidate` 不在 normalize-dom 的 ATTR_ALLOW 內（normalize-dom.ts:298-331），
  // 對 structure 比對沒有影響。
  // W11：action/method 在 ATTR_ALLOW 內＝進 structure 比對；白名單（legacy-form-specs）
  // 命中才帶（action="/system/" method="get"，organization.jsp:343 raw 原文）。
  // 批次 43：說明區畫在 form 之後（legacy 是 form 的 sibling，非子節點）；未傳＝零影響。
  return (
    <>
      <form
        data-ui-id={uiId}
        noValidate
        onSubmit={handleSubmit}
        className="flex flex-col gap-3"
        {...(formSpec?.action === undefined ? {} : { action: formSpec.action })}
        {...(formSpec?.method === undefined ? {} : { method: formSpec.method })}
      >
        {topActions === undefined ? null : topActions}
        <div data-ui-id={`${uiId}-fields`}>{children}</div>
        <div data-ui-id={`${uiId}-actions`} className="flex flex-wrap items-center gap-2">
          {extraActions}
          <Button
            uiId={`${uiId}-submit`}
            variant="primary"
            size="sm"
            type="submit"
            disabled={busy}
            // exactOptionalPropertyTypes：沒有 searchEffect 時要「整個不傳」而不是傳 undefined。
            {...(searchEffect === undefined ? {} : { dataEffect: searchEffect })}
          >
            {t('action.search')}
          </Button>
          {tailActions === undefined ? null : tailActions}
          {onReset === undefined ? null : (
            <Button
              uiId={`${uiId}-reset`}
              variant="default"
              size="sm"
              type="button"
              disabled={busy}
              onClick={onReset}
            >
              {t('action.reset')}
            </Button>
          )}
        </div>
      </form>
      {asideNote === undefined ? null : asideNote}
    </>
  );
}
